import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Topbar from '@/components/platform/Topbar';
import { LoadingScreen, RequireApprovedMember } from '@/components/platform/Guards';
import InvitationView from '@/components/wedding/InvitationView';
import { useAuth } from '@/context/AuthProvider';
import { ensureInvitation, saveInvitation } from '@/lib/weddingApi';
import { clearDraft, readDraft, writeDraft } from '@/lib/draftStore';
import { inviteShareUrl } from '@/lib/share';
import {
  emptyInvitationData,
  normalizeInvitationData,
  type InvitationData,
  type ThemeKey,
  type WeddingInvitation,
} from '@/types/wedding';
import InfoForm from './components/InfoForm';
import PhotoForm from './components/PhotoForm';
import MusicForm from './components/MusicForm';
import ThemePicker from './components/ThemePicker';

type TabKey = 'info' | 'photo' | 'theme';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'info', label: '정보 입력', icon: 'ri-edit-2-line' },
  { key: 'photo', label: '사진 · 음악', icon: 'ri-image-add-line' },
  { key: 'theme', label: '디자인 선택', icon: 'ri-palette-line' },
];

function formatClock(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function EditorBody() {
  const { member } = useAuth();
  const ownerId = member?.id ?? '';

  const [invitation, setInvitation] = useState<WeddingInvitation | null>(null);
  const [data, setData] = useState<InvitationData>(emptyInvitationData());
  const [theme, setTheme] = useState<ThemeKey>('classic-dark');
  const [published, setPublished] = useState(true);
  const [savedData, setSavedData] = useState<InvitationData>(emptyInvitationData());
  const [savedTheme, setSavedTheme] = useState<ThemeKey>('classic-dark');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState<TabKey>('info');
  const [draftRestored, setDraftRestored] = useState(false);
  const [autosavedAt, setAutosavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    setError('');
    try {
      const inv = await ensureInvitation(ownerId);
      const serverData = normalizeInvitationData(inv.data);
      const draft = await readDraft(ownerId);

      let restored = false;
      if (draft && typeof draft.savedAt === 'number') {
        const draftData = normalizeInvitationData(draft.data);
        const draftTheme = (draft.theme as ThemeKey) || inv.theme;
        const differs =
          JSON.stringify(draftData) !== JSON.stringify(serverData) ||
          draftTheme !== inv.theme ||
          draft.published !== inv.published;
        if (differs) {
          setData(draftData);
          setTheme(draftTheme);
          setPublished(typeof draft.published === 'boolean' ? draft.published : inv.published);
          setAutosavedAt(draft.savedAt);
          restored = true;
        }
      }

      if (!restored) {
        setData(serverData);
        setTheme(inv.theme);
        setPublished(inv.published);
        setAutosavedAt(null);
      }

      setInvitation(inv);
      setSavedData(serverData);
      setSavedTheme(inv.theme);
      setDraftRestored(restored);
    } catch (err) {
      setError(err instanceof Error ? err.message : '청첩장을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(
    () => theme !== savedTheme || JSON.stringify(data) !== JSON.stringify(savedData),
    [theme, savedTheme, data, savedData],
  );

  // 최신 편집 상태를 참조하기 위한 ref (자동 저장 & 페이지 이탈 시 flush 용)
  const latestRef = useRef({ ownerId, data, theme, published });
  useEffect(() => {
    latestRef.current = { ownerId, data, theme, published };
  }, [ownerId, data, theme, published]);

  // 입력이 멈추면 잠시 후 자동으로 임시 저장
  useEffect(() => {
    if (loading || !invitation || !ownerId || !dirty) return;
    const timer = window.setTimeout(() => {
      void writeDraft({ ownerId, data, theme, published, savedAt: Date.now() });
      setAutosavedAt(Date.now());
    }, 800);
    return () => window.clearTimeout(timer);
  }, [loading, invitation, ownerId, dirty, data, theme, published]);

  // 창을 최소화하거나 닫을 때 즉시 임시 저장 (긴 입력 중 이탈 대비)
  useEffect(() => {
    const flush = () => {
      const s = latestRef.current;
      if (!s.ownerId) return;
      void writeDraft({
        ownerId: s.ownerId,
        data: s.data,
        theme: s.theme,
        published: s.published,
        savedAt: Date.now(),
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const discardDraft = async () => {
    await clearDraft(ownerId);
    setData(savedData);
    setTheme(savedTheme);
    setDraftRestored(false);
    setAutosavedAt(null);
    setNotice('저장된 내용으로 되돌렸어요. 편집 내용은 초기화됐습니다.');
    window.setTimeout(() => setNotice(''), 2600);
  };

  const handleSave = async () => {
    if (!invitation) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const updated = await saveInvitation(invitation.id, { theme, data, published });
      setInvitation(updated);
      setSavedData(normalizeInvitationData(updated.data));
      setSavedTheme(updated.theme);
      await clearDraft(ownerId);
      setDraftRestored(false);
      setAutosavedAt(null);
      setNotice('저장했어요. 청첩장에 반영됐습니다.');
      window.setTimeout(() => setNotice(''), 2600);
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = invitation ? inviteShareUrl(invitation.slug) : '';

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setNotice('공유 링크를 복사했어요.');
      window.setTimeout(() => setNotice(''), 2200);
    } catch {
      setError('링크를 복사하지 못했어요. 직접 선택해 복사해 주세요.');
    }
  };

  if (loading) return <LoadingScreen label="청첩장을 준비하고 있어요…" />;

  return (
    <div className="pf-page">
      <Topbar />
      <main className="pf-main">
        <div className="ed-wrap">
          <div className="ed-head">
            <div>
              <h1>청첩장 만들기</h1>
              <p>
                정보를 입력하고 사진을 올린 뒤, 마음에 드는 디자인을 고르면 나만의 청첩장이 완성돼요.
              </p>
            </div>
            <div className="ed-head-actions">
              {dirty ? (
                <span className="ed-saved">
                  <i className="ri-error-warning-line" aria-hidden="true" />
                  저장하지 않은 변경사항이 있어요
                  {autosavedAt ? (
                    <span className="ed-autosave">
                      <i className="ri-history-line" aria-hidden="true" />
                      {formatClock(autosavedAt)} 자동 저장됨
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="ed-saved">
                  <i className="ri-checkbox-circle-line" aria-hidden="true" />
                  모든 변경사항이 저장됐어요
                </span>
              )}
              <button
                type="button"
                className="pf-btn primary"
                onClick={() => void handleSave()}
                disabled={saving || !invitation}
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line spin-anim" aria-hidden="true" />
                    저장 중…
                  </>
                ) : (
                  <>
                    <i className="ri-save-line" aria-hidden="true" />
                    저장하기
                  </>
                )}
              </button>
            </div>
          </div>

          {notice ? (
            <div className="pf-alert ok" role="status" style={{ marginBottom: 16 }}>
              <i className="ri-checkbox-circle-line" aria-hidden="true" />
              <span>{notice}</span>
            </div>
          ) : null}
          {error ? (
            <div className="pf-alert err" role="alert" style={{ marginBottom: 16 }}>
              <i className="ri-error-warning-line" aria-hidden="true" />
              <span>{error}</span>
              <button
                type="button"
                className="pf-btn sm ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => void load()}
              >
                다시 시도
              </button>
            </div>
          ) : null}
          {draftRestored ? (
            <div className="pf-alert ok" role="status" style={{ marginBottom: 16 }}>
              <i className="ri-history-line" aria-hidden="true" />
              <span>
                이전에 작성하던 내용을 불러왔어요. 이어서 편집한 뒤 저장해 주세요.
              </span>
              <button
                type="button"
                className="pf-btn sm ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => void discardDraft()}
              >
                저장된 내용으로 되돌리기
              </button>
            </div>
          ) : null}

          <div className="ed-layout">
            {/* FORM */}
            <div>
              <div className="ed-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    className={`ed-tab ${tab === t.key ? 'is-active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    <i className={t.icon} aria-hidden="true" />
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>

              {tab === 'info' ? <InfoForm data={data} setData={setData} /> : null}
              {tab === 'photo' ? (
                <>
                  <PhotoForm
                    photos={data.photos}
                    ownerId={ownerId}
                    onChange={(photos) => setData((prev) => ({ ...prev, photos }))}
                  />
                  <MusicForm
                    musicUrl={data.musicUrl}
                    ownerId={ownerId}
                    onChange={(url) => setData((prev) => ({ ...prev, musicUrl: url }))}
                  />
                </>
              ) : null}
              {tab === 'theme' ? <ThemePicker theme={theme} onChange={setTheme} /> : null}
            </div>

            {/* PREVIEW */}
            <aside className="ed-preview-col">
              <div className="ed-preview-head">
                <h2>
                  <i className="ri-smartphone-line" aria-hidden="true" />
                  실시간 미리보기
                </h2>
                {shareUrl ? (
                  <Link to={`/invite/${invitation?.slug ?? ''}`} target="_blank" rel="noreferrer">
                    <i className="ri-external-link-line" aria-hidden="true" />
                    새 창에서
                  </Link>
                ) : null}
              </div>

              <div className="phone">
                <div className="phone-screen">
                  <span className="phone-notch" aria-hidden="true" />
                  <InvitationView
                    data={data}
                    theme={theme}
                    interactive={false}
                    invitationId={invitation?.id}
                    shareUrl={shareUrl}
                  />
                </div>
              </div>

              <div className="ed-tip">
                {published ? (
                  <>
                    <i className="ri-global-line" aria-hidden="true" /> 지금 공개 상태예요. 아래 링크를
                    하객에게 공유하면 돼요.
                  </>
                ) : (
                  <>
                    <i className="ri-eye-off-line" aria-hidden="true" /> 비공개 상태예요. 공개로
                    전환하면 링크로 열람할 수 있어요.
                  </>
                )}
              </div>

              <div className="ed-stack" style={{ marginTop: 14 }}>
                <div className="ed-field">
                  <span className="ed-label">공유 링크</span>
                  <input className="ed-input" readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="pf-btn sm" onClick={() => void handleCopyLink()}>
                    <i className="ri-link" aria-hidden="true" />
                    링크 복사
                  </button>
                  <button
                    type="button"
                    className="pf-btn sm"
                    onClick={() => setPublished((v) => !v)}
                  >
                    <i className={published ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden="true" />
                    {published ? '비공개로 전환' : '공개로 전환'}
                  </button>
                </div>
                <p className="pf-muted" style={{ fontSize: 12.5 }}>
                  공개 여부를 바꾼 뒤에는 꼭 <strong>저장하기</strong>를 눌러 주세요.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function EditorPage() {
  return (
    <RequireApprovedMember>
      <EditorBody />
    </RequireApprovedMember>
  );
}